import { DisputeStatus, type Dispute } from "@/interfaces/dispute";
import {
  TradeMessageType,
  type TradeMessage,
} from "@/interfaces/message/message";
import { type Trade } from "@/interfaces/trade";
import { useAppSelector } from "@/store/hooks";
import {
  CheckCircle2,
  ImageIcon,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { useMemo, useState } from "react";
import { formatChatTime } from "@/utils";
import { cn } from "@/lib/utils";

interface Props {
  msg: TradeMessage;
  dispute: Dispute | null | undefined;
  trade: Trade;
  moderatorView?: boolean;
}

const TradeMessageItem = ({
  msg,
  dispute,
  trade,
  moderatorView = false,
}: Props) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);

  const userId = user?.id ?? "";
  const isBuyer = trade?.buyer?.id === msg.senderId;
  const isSeller = trade?.seller?.id === msg.senderId;

  const senderRole = isBuyer ? "Buyer" : isSeller ? "Seller" : "Participant";

  const senderName = isBuyer
    ? `${trade.buyer?.firstName ?? ""} ${trade.buyer?.lastName ?? ""}`.trim() ||
      "Buyer"
    : isSeller
      ? `${trade.seller?.firstName ?? ""} ${
          trade.seller?.lastName ?? ""
        }`.trim() || "Seller"
      : "Participant";

  const [selectedImagePreview, setSelectedImagePreview] = useState<{
    url: string;
    index: number;
  } | null>(null);

  const disputeImages = dispute?.images ?? [];

  const disputeOpeningMessageId = useMemo(() => {
    if (!trade?.messages?.length || !dispute) return null;

    const disputeMessage = trade.messages.find(
      (message) =>
        message.type === TradeMessageType.SYSTEM &&
        (message.systemBuyerMessage?.toLowerCase().includes("dispute") ||
          message.systemSellerMessage?.toLowerCase().includes("dispute")),
    );

    return disputeMessage?.id ?? null;
  }, [trade?.messages, dispute]);

  const renderDisputeCaseCard = () => {
    if (!dispute) return null;

    const statusColor =
      dispute.status === DisputeStatus.RESOLVED
        ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
        : "bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400";

    return (
      <div className="mt-3 w-full overflow-hidden rounded-xl border border-red-500/20 bg-background shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 bg-[crimson] px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
              <ShieldAlert className="h-4 w-4 text-white" />
            </div>

            <div className="min-w-0 text-left">
              <p className="truncate text-sm font-semibold text-white">
                Dispute Case Opened
              </p>

              <p className="text-[10px] text-red-100">Official BuyFiat Case</p>
            </div>
          </div>

          <Badge
            className={cn(
              "shrink-0 border px-2 py-0.5 text-[10px] font-medium",
              statusColor,
            )}
          >
            {dispute.status.replaceAll("_", " ")}
          </Badge>
        </div>

        {/* Body */}
        <div className="space-y-3.5 p-4">
          {/* Case Details */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="w-full">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Reason
              </p>

              <p className="text-xs leading-5 text-foreground">
                {dispute.reason}
              </p>
            </div>

            {dispute.resolution && (
              <div className="min-w-0">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Resolution
                </p>

                <p className="text-xs leading-5 text-foreground">
                  {dispute.resolution}
                </p>
              </div>
            )}
          </div>

          {/* Outcome */}
          {dispute.outcome && (
            <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Outcome
              </p>

              <Badge
                className={cn(
                  "border px-2 py-0.5 text-[10px] font-semibold",
                  dispute.outcome === "SELLER_WON"
                    ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    : "border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-400",
                )}
              >
                {dispute.outcome.replaceAll("_", " ")}
              </Badge>
            </div>
          )}

          {/* Timeline */}
          <div className="rounded-lg border">
            <div className="flex items-center justify-between border-b bg-muted/20 px-3 py-2">
              <p className="text-xs font-semibold">Case Timeline</p>

              <span className="text-[10px] text-muted-foreground">
                {dispute.resolvedAt ? "Closed" : "Active"}
              </span>
            </div>

            <div className="divide-y">
              {/* Submitted */}
              <div className="flex items-center gap-3 px-3 py-2.5">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500/10">
                  <CheckCircle2 className="h-3.5 w-3.5 text-red-500" />
                </div>

                <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <p className="text-xs font-medium">Dispute Submitted</p>

                  <p className="shrink-0 text-[10px] text-muted-foreground">
                    {new Date(dispute.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Resolved */}
              {dispute.resolvedAt && (
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  </div>

                  <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                    <p className="text-xs font-medium">Case Closed</p>

                    <p className="shrink-0 text-[10px] text-muted-foreground">
                      {new Date(dispute.resolvedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Evidence */}
          {disputeImages.length > 0 && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Submitted Evidence
                </p>

                <span className="text-[10px] text-muted-foreground">
                  {disputeImages.length}{" "}
                  {disputeImages.length === 1 ? "image" : "images"}
                </span>
              </div>

              <div className="grid grid-cols-4 gap-2">
                {disputeImages.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() =>
                      setSelectedImagePreview({
                        url: img,
                        index: idx,
                      })
                    }
                    className="group relative overflow-hidden rounded-lg border bg-muted/20"
                  >
                    <img
                      src={img}
                      alt={`Dispute evidence ${idx + 1}`}
                      className="h-16 w-full object-cover transition-opacity group-hover:opacity-80"
                    />

                    <span className="absolute bottom-1 right-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-medium text-white">
                      {idx + 1}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const messageAlignment =
    msg.type === TradeMessageType.SYSTEM || msg.type === TradeMessageType.ADMIN
      ? "justify-center"
      : moderatorView
        ? isBuyer
          ? "justify-start"
          : isSeller
            ? "justify-end"
            : "justify-start"
        : msg.senderId === userId
          ? "justify-end"
          : "justify-start";

  return (
    <div className={`flex ${messageAlignment}`}>
      {/* =========================================================
          SYSTEM MESSAGE
      ========================================================= */}
      {msg.type === TradeMessageType.SYSTEM ? (
        <div
          className={`px-4 py-3 rounded-lg text-xs text-center w-full space-y-2 ${
            theme === "dark"
              ? "bg-muted text-muted-foreground"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          <p>{isBuyer ? msg.systemBuyerMessage : msg.systemSellerMessage}</p>

          {msg.id === disputeOpeningMessageId && renderDisputeCaseCard()}

          {msg.id === disputeOpeningMessageId &&
            dispute &&
            disputeImages?.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2 mt-3">
                {disputeImages.map((img: string, idx: number) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() =>
                      setSelectedImagePreview({
                        url: img,
                        index: idx,
                      })
                    }
                    className="group"
                  >
                    <Badge
                      variant="secondary"
                      className="cursor-pointer px-3 py-1.5 transition-all hover:scale-[1.02]"
                    >
                      <ImageIcon className="h-3.5 w-3.5 mr-1" />
                      Image {idx + 1}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
        </div>
      ) : msg.type === TradeMessageType.ADMIN ? (
        /* =========================================================
           ADMIN MESSAGE
        ========================================================= */
        <div className="w-full flex justify-center">
          <div
            className={`w-full rounded-xl border px-4 py-4 shadow-sm ${
              theme === "dark"
                ? "bg-blue-950/30 border-blue-500/30"
                : "bg-blue-50 border-blue-200"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-500" />

                <div>
                  <p className="font-semibold text-sm">BuyFiat Support</p>

                  <p className="text-xs text-muted-foreground">
                    Official platform message
                  </p>
                </div>
              </div>

              <Badge
                variant="secondary"
                className="bg-blue-500/10 text-blue-600"
              >
                ADMIN
              </Badge>
            </div>

            {msg.message && (
              <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
            )}

            {msg.imageUrl && (
              <img
                src={msg.imageUrl}
                alt="BuyFiat Support attachment"
                className="mt-3 rounded-lg border max-h-72 object-cover"
              />
            )}

            <div className="mt-3 flex justify-end">
              <span className="text-[11px] text-muted-foreground">
                {formatChatTime(msg.createdAt)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        /* =========================================================
           BUYER / SELLER MESSAGE
        ========================================================= */
        <div
          className={`flex max-w-[80%] flex-col ${
            msg.senderId === userId ? "items-end" : "items-start"
          }`}
        >
          {/* Sender identity */}
          <div
            className={`mb-1 flex items-center gap-1.5 px-1 ${
              msg.senderId === userId ? "justify-end" : "justify-start"
            }`}
          >
            <span
              className={`text-[11px] font-semibold ${
                isBuyer
                  ? "text-emerald-600 dark:text-emerald-400"
                  : isSeller
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-muted-foreground"
              }`}
            >
              {senderRole}
            </span>

            <span className="text-[10px] text-muted-foreground">
              {senderName}
            </span>
          </div>

          {/* Message bubble */}
          <div
            className={`w-fit rounded-lg px-4 py-2 ${
              msg.senderId === userId
                ? "bg-[#F0B90B]/90 text-black"
                : theme === "dark"
                  ? "bg-muted text-foreground"
                  : "bg-gray-100 text-gray-900"
            }`}
          >
            {msg.message && (
              <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
            )}

            {msg.imageUrl && (
              <div className="mt-2">
                <img
                  src={msg.imageUrl}
                  alt={`${senderRole} attachment`}
                  className="rounded-lg max-h-60 object-cover border"
                />
              </div>
            )}

            <div className="mt-1 flex justify-end">
              <span
                className={`text-[10px] ${
                  msg.senderId === userId
                    ? "text-black/60"
                    : "text-muted-foreground"
                }`}
              >
                {formatChatTime(msg.createdAt)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeMessageItem;
